import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Pane from 'components/Common/Pane'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import formatOfferInclusionDescription from 'lib/offer/formatOfferInclusionDescription'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'

const Container = styled.div`
  margin-top: ${rem(12)};
`

const InclusionText = styled(BodyTextBlock)`
  flex: 1;
  min-width: 0;
`

const PopularLabel = styled(NeutralLabel)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

interface Props {
  checked?: boolean
  inclusion: App.PackageInclusion
  packageKey: string
  onChange: (packageKey: string, event: React.ChangeEvent<HTMLInputElement>) => void
  priceDifference: number
}

function AirportTransferOption(props: Props) {
  const {
    checked,
    inclusion,
    packageKey,
    onChange,
    priceDifference,
  } = props

  const intl = useIntl()

  const heading = intl.formatMessage({
    id: 'o3lwYgrkALDsCAU6Evtd',
    defaultMessage: 'Extras to elevate your stay',
    description: 'Heading above optional extra inclusions that can be added to a flash hotel package',
  })
  const popularLabel = intl.formatMessage({
    id: 'IrcNoG-CK1--BRZBTOe7',
    defaultMessage: 'Popular',
    description: 'Indicates that a package is recommended due to overall popularity',
  })
  const inclusionDescription = useMemo(
    () => formatOfferInclusionDescription(inclusion.description, intl),
    [inclusion.description, intl],
  )
  const onCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(packageKey, event)
  }, [onChange, packageKey])

  return (
    <Container>
      <Group direction="vertical" gap={8}>
        <BodyText variant="medium" weight="bold">{heading}</BodyText>
        <Pane background="highlight-primary" borderRadius="S" padding="12 16">
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
            <InclusionText variant="medium" startIcon={<LineTaxiIcon />}>
              {inclusionDescription}
              {priceDifference > 0 && <>
                {' '}
                <BodyText as="span" variant="medium" weight="bold" wrap="no-wrap">
                  <FormatCurrency value={priceDifference} format="roundedDollar" signDisplay="always" />
                </BodyText>
              </>}
            </InclusionText>
            <Group direction="horizontal" verticalAlign="center" gap={12}>
              <PopularLabel startIcon={<LineAwardIcon />}>
                {popularLabel}
              </PopularLabel>
              <CheckboxButton
                checked={checked}
                onChange={onCheckboxChange}
                size="medium"
              />
            </Group>
          </Group>
        </Pane>
      </Group>
    </Container>
  )
}

export default AirportTransferOption
